import { Link, useNavigate, useParams, useSearch } from "@tanstack/solid-router";
import { For, Show, createEffect, createSignal, onCleanup } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { HighlightMatch } from "~/components/HighlightMatch";
import { useSearch as useSearchQuery } from "~/lib/hooks";
import type { AttachableEntity, SearchCustomRecordHit } from "~/lib/types";

/**
 * Full-page search results. Mirrors the GlobalSearch popover but lifts the
 * per-section cap from 25 to 100 and gives results room to breathe.
 *
 * `q` lives in the URL so results are sharable/bookmarkable. The visible
 * search input is debounced (250ms) and writes back to the URL so refining
 * doesn't spam requests.
 */
export function SearchResultsPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const urlSearch = useSearch({ strict: false }) as () => { q?: string };
  const navigate = useNavigate();

  const urlQ = () => urlSearch().q ?? "";
  const [raw, setRaw] = createSignal(urlQ());
  const [debounced, setDebounced] = createSignal(urlQ());

  // Debounce keystrokes before writing back to the URL and refetching.
  createEffect(() => {
    const v = raw();
    const t = window.setTimeout(() => {
      setDebounced(v);
      if (v !== urlQ()) {
        void navigate({
          to: "/workspaces/$wsKey/search",
          params: { wsKey: params().wsKey },
          search: v ? { q: v } : {},
          replace: true,
        });
      }
    }, 250);
    onCleanup(() => clearTimeout(t));
  });

  const results = useSearchQuery(
    () => params().wsKey,
    debounced,
    () => 100,
  );

  const totalCount = () => {
    const r = results.data;
    if (!r) return 0;
    return (
      r.accounts.length +
      r.contacts.length +
      r.custom_records.length +
      r.notes.length +
      r.activities.length
    );
  };

  return (
    <AppShell wsKey={params().wsKey} pageTitle="Search">
      <div class="flex flex-col gap-4">
        <div>
          <input
            type="search"
            class="input input-bordered w-full max-w-xl"
            placeholder="Search workspace…"
            aria-label="Search workspace"
            value={raw()}
            autofocus
            onInput={(e) => setRaw(e.currentTarget.value)}
          />
          <Show when={debounced().length > 0 && results.data}>
            <p class="text-sm opacity-60 mt-2">
              {totalCount()} {totalCount() === 1 ? "match" : "matches"} for "{debounced()}". Each
              section capped at 100.
            </p>
          </Show>
        </div>

        <Show when={debounced().length === 0}>
          <p class="opacity-60">Type a query to search.</p>
        </Show>

        <Show when={debounced().length > 0 && results.isLoading}>
          <p role="status" aria-live="polite" class="opacity-60">
            Searching…
          </p>
        </Show>

        <Show when={debounced().length > 0 && results.data && totalCount() === 0}>
          <p class="opacity-60">No matches.</p>
        </Show>

        <Show when={(results.data?.accounts.length ?? 0) > 0}>
          <Section label="Accounts" count={results.data?.accounts.length ?? 0}>
            <For each={results.data?.accounts}>
              {(a) => (
                <Link
                  to="/workspaces/$wsKey/accounts/$accId"
                  params={{ wsKey: params().wsKey, accId: a.id }}
                  class="block px-3 py-2 rounded-md hover:bg-base-200"
                >
                  <div class="font-medium">
                    <HighlightMatch text={a.name} query={debounced()} />
                  </div>
                  <Show when={a.primary_domain || a.primary_email}>
                    <div class="text-xs opacity-60 truncate">
                      <HighlightMatch
                        text={a.primary_domain ?? a.primary_email ?? ""}
                        query={debounced()}
                      />
                    </div>
                  </Show>
                </Link>
              )}
            </For>
          </Section>
        </Show>

        <Show when={(results.data?.contacts.length ?? 0) > 0}>
          <Section label="Contacts" count={results.data?.contacts.length ?? 0}>
            <For each={results.data?.contacts}>
              {(c) => (
                <Link
                  to="/workspaces/$wsKey/contacts/$conId"
                  params={{ wsKey: params().wsKey, conId: c.id }}
                  class="block px-3 py-2 rounded-md hover:bg-base-200"
                >
                  <div class="font-medium">
                    <HighlightMatch text={c.full_name} query={debounced()} />
                  </div>
                  <Show when={c.primary_email}>
                    <div class="text-xs opacity-60 truncate">
                      <HighlightMatch text={c.primary_email ?? ""} query={debounced()} />
                    </div>
                  </Show>
                </Link>
              )}
            </For>
          </Section>
        </Show>

        <Show when={(results.data?.custom_records.length ?? 0) > 0}>
          <Section label="Custom records" count={results.data?.custom_records.length ?? 0}>
            <For each={results.data?.custom_records}>
              {(r) => <CustomRecordRow wsKey={() => params().wsKey} row={r} query={debounced()} />}
            </For>
          </Section>
        </Show>

        <Show when={(results.data?.notes.length ?? 0) > 0}>
          <Section label="Notes" count={results.data?.notes.length ?? 0}>
            <For each={results.data?.notes}>
              {(n) => (
                <AttachedRow
                  wsKey={() => params().wsKey}
                  entityType={n.attached_to_entity_type}
                  entityId={n.attached_to_entity_id}
                  objectKey={n.attached_object_key}
                  title={n.body}
                  subtitle="note"
                  query={debounced()}
                />
              )}
            </For>
          </Section>
        </Show>

        <Show when={(results.data?.activities.length ?? 0) > 0}>
          <Section label="Activities" count={results.data?.activities.length ?? 0}>
            <For each={results.data?.activities}>
              {(a) => (
                <AttachedRow
                  wsKey={() => params().wsKey}
                  entityType={a.primary_entity_type}
                  entityId={a.primary_entity_id}
                  objectKey={a.primary_object_key}
                  title={a.subject}
                  body={a.body}
                  subtitle={a.kind}
                  query={debounced()}
                />
              )}
            </For>
          </Section>
        </Show>
      </div>
    </AppShell>
  );
}

function Section(props: { label: string; count: number; children: any }) {
  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body p-3">
        <div class="flex items-center justify-between px-1 mb-1">
          <h3 class="font-medium">{props.label}</h3>
          <span class="text-xs opacity-50">{props.count}</span>
        </div>
        <div class="divide-y divide-base-200">{props.children}</div>
      </div>
    </div>
  );
}

function CustomRecordRow(props: {
  wsKey: () => string;
  row: SearchCustomRecordHit;
  query: string;
}) {
  return (
    <Link
      to="/workspaces/$wsKey/custom/$objectKey/$recordId"
      params={{
        wsKey: props.wsKey(),
        objectKey: props.row.object_key,
        recordId: props.row.id,
      }}
      class="block px-3 py-2 rounded-md hover:bg-base-200"
    >
      <div class="font-medium">
        <HighlightMatch text={props.row.display_name || props.row.name} query={props.query} />
      </div>
      <div class="text-xs opacity-60">{props.row.object_label_singular}</div>
    </Link>
  );
}

function AttachedRow(props: {
  wsKey: () => string;
  entityType: AttachableEntity;
  entityId: string;
  objectKey: string | null;
  title: string;
  body?: string | null;
  subtitle: string;
  query: string;
}) {
  const inner = () => (
    <>
      <div class="font-medium">
        <HighlightMatch text={props.title} query={props.query} />
      </div>
      <Show when={props.body}>
        {(b) => (
          <div class="text-xs opacity-70">
            <HighlightMatch text={b()} query={props.query} />
          </div>
        )}
      </Show>
      <div class="text-xs opacity-60">
        {props.subtitle} · on{" "}
        {props.entityType === "custom_record" ? "custom record" : props.entityType}
      </div>
    </>
  );
  if (props.entityType === "account") {
    return (
      <Link
        to="/workspaces/$wsKey/accounts/$accId"
        params={{ wsKey: props.wsKey(), accId: props.entityId }}
        class="block px-3 py-2 rounded-md hover:bg-base-200"
      >
        {inner()}
      </Link>
    );
  }
  if (props.entityType === "contact") {
    return (
      <Link
        to="/workspaces/$wsKey/contacts/$conId"
        params={{ wsKey: props.wsKey(), conId: props.entityId }}
        class="block px-3 py-2 rounded-md hover:bg-base-200"
      >
        {inner()}
      </Link>
    );
  }
  if (props.entityType === "deal") {
    return (
      <Link
        to="/workspaces/$wsKey/deals/$dealId"
        params={{ wsKey: props.wsKey(), dealId: props.entityId }}
        class="block px-3 py-2 rounded-md hover:bg-base-200"
      >
        {inner()}
      </Link>
    );
  }
  if (props.entityType === "custom_record" && props.objectKey) {
    return (
      <Link
        to="/workspaces/$wsKey/custom/$objectKey/$recordId"
        params={{
          wsKey: props.wsKey(),
          objectKey: props.objectKey,
          recordId: props.entityId,
        }}
        class="block px-3 py-2 rounded-md hover:bg-base-200"
      >
        {inner()}
      </Link>
    );
  }
  return <div class="block px-3 py-2 rounded-md">{inner()}</div>;
}
